import { deleteField } from 'firebase/firestore';
import type { EntityEventChange, Event, EventEntityChange, FieldChange } from '@/lib/types';

/**
 * Checks if a FieldChange references a specific entity ID in identifier, value, or newValue.
 */
export function isChangeReferencingEntity(change: FieldChange, targetEntityId: string): boolean {
  if (change.identifier === targetEntityId) return true;

  const extractId = (val: unknown) => {
    if (!val) return undefined;
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      const r = val as Record<string, unknown>;
      return (r.characterId as string) || (r.id as string) || (r.organizationId as string) || (r.locationId as string);
    }
    return undefined;
  };

  if (extractId(change.value) === targetEntityId) return true;
  if (extractId(change.newValue) === targetEntityId) return true;

  return false;
}

/**
 * Removes any FieldChange referencing targetEntityId from an entity's eventChanges list.
 * Returns null if no changes were needed.
 */
export function cleanupEntityEventChangesForDeletedEntity(
  eventChanges: EntityEventChange[] | undefined,
  deletedEntityId: string
): { updatedChanges: EntityEventChange[]; affectedEventIds: string[] } | null {
  if (!Array.isArray(eventChanges) || eventChanges.length === 0) return null;

  let hasModifications = false;
  const affectedEventIds: string[] = [];

  const updatedChanges = eventChanges.map((ec) => {
    const remainingFieldChanges = ec.changes.filter((c) => !isChangeReferencingEntity(c, deletedEntityId));
    if (remainingFieldChanges.length !== ec.changes.length) {
      hasModifications = true;
      if (remainingFieldChanges.length === 0) {
        affectedEventIds.push(ec.eventId);
      }
      return { ...ec, changes: remainingFieldChanges };
    }
    return ec;
  }).filter((ec) => ec.changes.length > 0);

  if (!hasModifications) return null;

  return { updatedChanges, affectedEventIds };
}

/**
 * Builds the minimal updates required to unlink a deleted entity from an event
 * and remove any eventChanges associated with that entity.
 * Returns null if no cleanup is needed for this event.
 */
export function getEventEntityCleanup(
  event: Event,
  deletedEntityId: string
): Record<string, unknown> | null {
  const updates: Record<string, unknown> = {};

  if (Array.isArray(event.characterIds) && event.characterIds.includes(deletedEntityId)) {
    updates.characterIds = event.characterIds.filter((id) => id !== deletedEntityId);
  }

  if (Array.isArray(event.organizationIds) && event.organizationIds.includes(deletedEntityId)) {
    updates.organizationIds = event.organizationIds.filter((id) => id !== deletedEntityId);
  }

  if (Array.isArray(event.locationIds) && event.locationIds.includes(deletedEntityId)) {
    updates.locationIds = event.locationIds.filter((id) => id !== deletedEntityId);
  }

  if (event.primaryLocationId === deletedEntityId) {
    updates.primaryLocationId = deleteField();
  }

  const rawEvent = event as Record<string, unknown>;
  if (typeof rawEvent.locationId === 'string' && rawEvent.locationId === deletedEntityId) {
    updates.locationId = deleteField();
  }

  if (Array.isArray(event.eventChanges)) {
    const hasMatchingChange = event.eventChanges.some(
      (change: EventEntityChange) => change.entityId === deletedEntityId
    );
    if (hasMatchingChange) {
      updates.eventChanges = event.eventChanges.filter(
        (change: EventEntityChange) => change.entityId !== deletedEntityId
      );
    }
  }

  return Object.keys(updates).length > 0 ? updates : null;
}
